import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSystemAdmin?: boolean;
  requireStorageAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireSystemAdmin = false,
  requireStorageAdmin = false,
}) => {
  const { user, loading, getAccessToken } = useAuth();
  const location = useLocation();
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  // Check token validity on mount and when user changes
  useEffect(() => {
    const checkToken = async () => {
      if (user) {
        const token = await getAccessToken();
        setTokenValid(!!token);
      } else {
        setTokenValid(false);
      }
    };

    checkToken();
  }, [user, getAccessToken]);

  if (loading || tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !tokenValid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireSystemAdmin && !user.isSystemAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">You need System Administrator privileges to access this page.</p>
          <Navigate to="/dashboard" replace />
        </div>
      </div>
    );
  }

  if (requireStorageAdmin && !user.isStorageAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">You need Storage Administrator privileges to access this page.</p>
          <Navigate to="/dashboard" replace />
        </div>
      </div>
    );
  }

  return <>{children}</>;
};